import React from 'react';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * The label to display next to the checkbox
   */
  label?: React.ReactNode;
  /**
   * Alternative way to provide label as children
   */
  children?: React.ReactNode;
  /**
   * Display the checkbox inline (default is block)
   */
  inline?: boolean;
  /**
   * Optional inline style overrides for the container
   */
  style?: React.CSSProperties;
  /**
   * Optional inline style overrides for the label
   */
  labelStyle?: React.CSSProperties;
}

/**
 * A Checkbox replacement for react-bootstrap's <Checkbox>.
 * Replicates Bootstrap 3's .checkbox and .checkbox-inline styling with inline styles.
 *
 * Block mode: <div class="checkbox"><label><input>Label</label></div>
 * Inline mode: <label class="checkbox-inline"><input>Label</label>
 */
const Checkbox: React.FC<CheckboxProps> = ({
  label,
  children,
  inline = false,
  style = {},
  labelStyle = {},
  ...props
}) => {
  const displayLabel = label || children;

  // If no label, render just the input for flexbox compatibility
  if (!displayLabel) {
    const plainInputStyle: React.CSSProperties = {
      margin: 0,
      padding: 0,
      display: 'block',
      ...style,
    };
    return <input type="checkbox" style={plainInputStyle} {...props} />;
  }

  // Checkbox input styles (same for both inline and block)
  const inputStyle: React.CSSProperties = {
    position: 'absolute',
    marginLeft: -20,
    top: '-2px',
  };

  // Label styles
  const baseLabelStyle: React.CSSProperties = {
    minHeight: 20,
    paddingLeft: 20,
    marginBottom: 0,
    fontWeight: 'normal',
    cursor: 'pointer',
    ...labelStyle,
  };

  const inputElement = <input type="checkbox" style={inputStyle} {...props} />;

  if (inline) {
    // Inline mode: <label class="checkbox-inline">
    const inlineLabelStyle: React.CSSProperties = {
      ...baseLabelStyle,
      position: 'relative',
      display: 'inline-block',
      verticalAlign: 'middle',
      ...style,
    };

    return (
      // eslint-disable-next-line jsx-a11y/label-has-for
      <label style={inlineLabelStyle}>
        {inputElement}
        {displayLabel}
      </label>
    );
  }

  // Block mode: <div class="checkbox"><label>
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'block',
    marginTop: 10,
    marginBottom: 10,
    ...style,
  };

  const blockLabelStyle: React.CSSProperties = {
    ...baseLabelStyle,
  };

  return (
    <div style={containerStyle}>
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      <label style={blockLabelStyle}>
        {inputElement}
        {displayLabel}
      </label>
    </div>
  );
};

export default Checkbox;
